import SingleFilledStar from "assets/icons/walkthrough/SingleFilledStar";
import SingleOutlinedStar from "assets/icons/walkthrough/SingleOutlinedStar";
import TitleStars from "assets/icons/walkthrough/TitleStars";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAppSelector } from "store/hooks";
import { getSettings } from "store/slices";
import WalkthroughRegister from "assets/images/walkthrough/walkthrough_register.png";
import WalkthroughWelcome from "assets/images/walkthrough/walkthroug_welcome.png";
import { useAllQuestionnaire } from "api/questionnaire";

const getbgImage = (walk_through, page) => {
  if (!walk_through) return "";
  if (page === 1)
    return walk_through?.page_one?.cover_media_URL
      ? process.env.REACT_APP_CLOUDFRONT_URL +
          walk_through.page_one.cover_media_URL
      : "";
  if (page === 2)
    return walk_through?.page_two?.cover_media_URL
      ? process.env.REACT_APP_CLOUDFRONT_URL +
          walk_through.page_two.cover_media_URL
      : "";
  if (page === 3)
    return walk_through?.page_three?.cover_media_URL
      ? process.env.REACT_APP_CLOUDFRONT_URL +
          walk_through.page_three.cover_media_URL
      : "";
  return "";
};

const Walkthrough = ({ SetWalkThroughDone }) => {
  const [view, setView] = useState(1);
  const [imageLoading, setImageLoading] = useState(false);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);
  const {
    dir,
    tenant: { walk_through = null },
  } = useAppSelector(getSettings);
  const { data: questionnaire } = useAllQuestionnaire();

  // Extract all image URLs from walk_through object
  const getAllImageUrls = (walkThrough) => {
    if (!walkThrough) return [];
    const urls = [];

    if (walkThrough.page_one?.cover_media_URL) {
      urls.push(
        process.env.REACT_APP_CLOUDFRONT_URL + walkThrough.page_one.cover_media_URL
      );
    }
    if (walkThrough.page_two?.cover_media_URL) {
      urls.push(
        process.env.REACT_APP_CLOUDFRONT_URL + walkThrough.page_two.cover_media_URL
      );
    }
    if (walkThrough.page_three?.cover_media_URL) {
      urls.push(
        process.env.REACT_APP_CLOUDFRONT_URL + walkThrough.page_three.cover_media_URL
      );
    }

    return urls;
  };

  // Preload all images when component mounts
  useEffect(() => {
    const imageUrls = getAllImageUrls(walk_through);

    if (imageUrls.length === 0) {
      setAllImagesLoaded(true);
      return;
    }

    let loadedCount = 0;
    const totalImages = imageUrls.length;

    imageUrls.forEach((url) => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setAllImagesLoaded(true);
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setAllImagesLoaded(true);
        }
      };
      img.src = url;
    });
  }, [walk_through]);

  const bgImage = useMemo(
    () => getbgImage(walk_through, view),
    [walk_through, view]
  );

  return (
    <>
      <div
        className="fixed z-50 top-0 left-0 h-dvh w-dvw bg-black bg-opacity-30 flex items-center justify-center"
        dir={dir}
      >
        <div
          style={
            bgImage && allImagesLoaded
              ? {
                  backgroundImage: `url(${bgImage})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }
              : {}
          }
          className="md:rounded-3xl relative"
        >
          {/* Skeleton loader - show while images are loading */}
          {!allImagesLoaded && bgImage && (
            <div className="absolute inset-0 bg-gray-300 animate-pulse md:rounded-3xl">
              <div className="h-full w-full bg-gradient-to-br from-gray-200 to-gray-400 md:rounded-3xl"></div>
            </div>
          )}

          <div
            className={
              "h-dvh w-dvw md:h-[90vh] md:w-[80vw] md:rounded-3xl flex flex-col items-center bg-opacity-80 " +
              (bgImage && allImagesLoaded
                ? "bg-transparent"
                : "bg-gradient-to-r from-[rgb(78,74,226)] to-[rgb(75,136,227)]")
            }
          >
            {view === 3 ? (
              <RegisterScreen
                SetWalkThroughDone={SetWalkThroughDone}
                personalAI={questionnaire?.data?._id}
                walk_through={walk_through?.page_three}
              />
            ) : (
              <IntroScreen view={view} setView={setView} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Walkthrough;

const RegisterScreen = ({ SetWalkThroughDone, personalAI, walk_through }) => {
  return (
    <>
      <div className="my-auto flex flex-col items-center">
        <h1 className="text-4xl text-white font-bold text-center relative">
          {walk_through?.title || t("walktroughRegisterPageTitle")}
          <span className="absolute -top-10 -left-12">
            <TitleStars className="w-12" />
          </span>
        </h1>
        <p className="text-white text-xl font-normal text-center mt-5 w-[290px]">
          {walk_through?.subtitle || t("walktroughRegisterPagesubTitle")}
        </p>
      </div>
      <div className="w-full flex flex-col gap-6 mb-4 md:mb-20">
        <Link
          to="/register"
          className="mx-auto"
          onClick={() => {
            localStorage.setItem("walkthroughDone", true);
            SetWalkThroughDone(true);
          }}
        >
          <button className="bg-[#FACA20] max-w-xs w-80 rounded-full py-4 mx-auto shadow shadow-black flex justify-center items-center relative">
            <img
              src={WalkthroughRegister}
              alt="walkthrough"
              className="w-[60%] bottom-full absolute"
            />
            {walk_through?.button_text || t("walktroughRegisterButton")}
            <SingleFilledStar className="absolute -top-4 -left-1 w-[14px]" />
            <SingleFilledStar className="absolute -top-8 left-16" />
            <SingleOutlinedStar className="absolute -top-4 -right-2 w-[12px]" />
            <SingleOutlinedStar className="absolute -bottom-6 right-4" />
          </button>
        </Link>
        {/* <Link
          to="/user-auth/login"
          className="mx-auto"
          onClick={() => {
            localStorage.setItem("walkthroughDone", true);
            SetWalkThroughDone(true);
          }}
        >
          <button className="underline text-white">
            {t("walktroughLoginButton")}
          </button>
        </Link> */}
        {walk_through?.free_questionnaire ? (
          <Link
            to={`quiz/${personalAI}`}
            className="mx-auto"
            onClick={() => {
              localStorage.setItem("walkthroughDone", true);
              SetWalkThroughDone(true);
            }}
          >
            <button className="underline text-white mt-8">
              {t("freeQuizButton")}
            </button>
          </Link>
        ) : (
          <div></div>
        )}
      </div>
    </>
  );
};

const IntroScreen = ({ view, setView }) => {
  const { tenant: tenantDetail } = useAppSelector(getSettings);

  return (
    <>
      <div
        className={`${
          view === 1 ? "my-auto" : "h-full"
        } flex flex-col items-center`}
      >
        {view === 1 ? (
          <>
            <h1 className="text-4xl text-white font-bold text-center relative">
              {tenantDetail?.walk_through?.page_one.title ||
                t("walktroughWelcomeTitle")}
              <span className="absolute -top-14 -left-12">
                <TitleStars />
              </span>
            </h1>
            {tenantDetail?.walk_through?.page_one.subtitle ? (
              <p
                className="text-white text-xl font-normal text-center mt-5 w-[270px]"
                dangerouslySetInnerHTML={{
                  __html:
                    tenantDetail?.walk_through?.page_one.subtitle.replace(
                      "\n",
                      "<br />"
                    ) || t("walktroughWelcomeSubtitle"),
                }}
              ></p>
            ) : (
              <p className="text-white text-xl font-normal text-center mt-5 w-[270px]">
                {t("walktroughWelcomeSubtitle")}
                <br />
                {t("walktroughWelcomeSubtitle2")}
              </p>
            )}
          </>
        ) : (
          <>
            <h1 className="text-4xl text-white font-bold text-center relative mt-auto">
              {tenantDetail?.walk_through?.page_two.title ||
                t("walktroughAISimulatorTitle")}
              <span className="absolute -top-10 -left-12">
                <TitleStars className="w-12" />
              </span>
            </h1>
            {tenantDetail?.walk_through?.page_two.subtitle ? (
              <p className="text-white text-xl font-normal text-center mt-5 w-[280px] mx-auto">
                {tenantDetail?.walk_through?.page_two.subtitle ||
                  t("walktroughAISimulatorSubtitle", {
                    institute: tenantDetail?.institution_title,
                  })}
              </p>
            ) : null}
            <img
              src="/gifs/walkthrough.gif"
              className="h-[270px] my-auto"
              alt="walkthrough"
            />
          </>
        )}
      </div>
      <div className="w-full flex flex-col gap-6 mb-16 md:mb-20">
        <button
          className="bg-white max-w-xs w-[calc(100%-30px)] sm:w-full flex justify-center items-center rounded-full py-4 mx-auto shadow shadow-black relative"
          onClick={() => setView(view + 1)}
        >
          {view === 1 ? (
            <img
              src={WalkthroughWelcome}
              alt="welcome"
              className="absolute bottom-full w-[60%]"
            />
          ) : (
            ""
          )}
          {t(
            view === 1
              ? tenantDetail?.walk_through?.page_one.button_text ||
                  "walktroughWelcomeNextButton"
              : tenantDetail?.walk_through?.page_two.button_text ||
                  "walktroughWelcomeFinishButton"
          )}
        </button>
        <div className="mx-auto flex items-center justify-center gap-2.5">
          {[1, 2].map((ele) => (
            <div
              key={ele}
              className={`rounded-full p-0.5 border transition-all duration-300 ease-in-out ${
                ele === view ? "border-white" : "border-transparent"
              }`}
            >
              <div className="size-1 bg-white rounded-full p-0.5"></div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
